import { useState } from 'react';
import type { Player } from '../../../types/player';
import { PlayerPhoto } from '../../../components/PlayerPhoto';

interface Props {
  onSearch: (query: string) => Player[];
  onSelect: (player: Player) => void;
  placeholder?: string;
}

export function TradeSearch({
  onSearch,
  onSelect,
  placeholder = 'Search a player',
}: Props) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<Player[]>([]);
  const [open, setOpen] = useState(false);

  const handleChange = (value: string) => {
    setQuery(value);
    if (value.trim().length < 2) {
      setResults([]);
      setOpen(false);
      return;
    }
    setResults(onSearch(value));
    setOpen(true);
  };

  return (
    <div className="relative">
      <input
        type="text"
        className="w-full rounded-xl border border-white/10 bg-white/5 px-4 py-2.5 text-sm text-white placeholder:text-zinc-500"
        placeholder={placeholder}
        value={query}
        onChange={(event) => handleChange(event.target.value)}
        onFocus={() => {
          if (results.length > 0) setOpen(true);
        }}
        onBlur={() => setTimeout(() => setOpen(false), 180)}
        autoComplete="off"
      />
      {open && (
        <div className="absolute left-0 right-0 z-20 mt-1 max-h-72 overflow-y-auto rounded-xl border border-white/10 bg-ink shadow-xl">
          {results.length === 0 ? (
            <p className="px-4 py-3 text-sm text-zinc-500">No players found</p>
          ) : (
            results.map((player) => (
              <button
                key={String(player.id)}
                type="button"
                className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-white/5"
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => {
                  onSelect(player);
                  setQuery('');
                  setResults([]);
                  setOpen(false);
                }}
              >
                <PlayerPhoto
                  name={player.name}
                  position={player.position}
                  team={player.team}
                  sleeperId={player.id}
                  espnId={player.espn_id}
                  imageUrl={player.imageUrl}
                  className="h-7 w-7 rounded-full object-cover"
                />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm text-white">{player.name}</span>
                  <span className="block text-xs text-zinc-500">
                    {player.rank ? `#${player.rank} · ` : ''}
                    {player.position === 'DEF' ? 'D/ST' : player.position} · {player.team}
                  </span>
                </span>
                <span className="text-xs text-zinc-400">{playerTradeLabel(player)}</span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

function playerTradeLabel(player: Player): string {
  const value = player.value ?? 0;
  return value ? value.toFixed(1) : '';
}
